import { useEffect, useRef, useState } from 'react';

/** Tap a whole score; hold (or use the keyboard menu key) for quarter points. */
export function ScoreSegment({
  value,
  min = 1,
  max,
  label,
  onPick,
  disabled,
}: {
  value: number | null;
  min?: number;
  max: number;
  label: string;
  onPick: (value: number) => void;
  disabled?: boolean;
}) {
  const [base, setBase] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const held = useRef(false);
  const clear = () => clearTimeout(timer.current);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <div className="relative">
      <div className="flex flex-wrap gap-1">
        {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
          <button
            key={n}
            type="button"
            disabled={disabled}
            aria-label={`${label} ${n}; hold for quarter points`}
            aria-pressed={value != null && Math.floor(value) === n}
            onPointerDown={() => {
              held.current = false;
              timer.current = setTimeout(() => {
                held.current = true;
                setBase(n);
              }, 450);
            }}
            onPointerUp={clear}
            onPointerCancel={clear}
            onPointerLeave={clear}
            onContextMenu={(e) => {
              e.preventDefault();
              clear();
              held.current = true;
              setBase(n);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setBase(n);
              }
            }}
            onClick={() => {
              if (!held.current) onPick(n);
              held.current = false;
            }}
            className={`h-11 min-w-11 select-none rounded-md px-2 text-sm ${value != null && Math.floor(value) === n ? 'bg-wine-700 text-on-wine' : 'border border-ink-800 bg-ink-950 text-cream-300 disabled:opacity-50'}`}
          >
            {value != null && Math.floor(value) === n ? value : n}
          </button>
        ))}
      </div>
      {base != null && !disabled && (
        <div
          role="dialog"
          aria-label={`${label} quarter points`}
          className="absolute z-20 mt-1 flex gap-1 rounded-lg border border-gold-600 bg-ink-950 p-2 shadow-xl"
          onKeyDown={(e) => {
            if (e.key === 'Escape') setBase(null);
          }}
        >
          {[0, 0.25, 0.5, 0.75]
            .map((v) => base + v)
            .filter((v) => v <= max)
            .map((v, i) => (
              <button
                autoFocus={i === 0}
                className="btn-ghost px-3"
                key={v}
                onClick={() => {
                  onPick(v);
                  setBase(null);
                }}
              >
                {v.toFixed(v % 1 === 0 ? 1 : 2)}
              </button>
            ))}
          <button
            className="btn-ghost"
            aria-label="Close quarter points"
            onClick={() => setBase(null)}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
